import { Item, TableGridItem } from "@/models/table-grid-interface";

export const CONDITION_OPERATORS = ['<', '>', '=', '!=', '<=', '>=', 'contains', 'one_of'] as const;
export type Condition = typeof CONDITION_OPERATORS[number];
type Value = number | string | boolean | undefined | null;

export type ColorSchemeIn = string | {
  background?: string,
  title?: string,
  text?: string,
  badge?: string | Array<Badge | string>,
};

// Standard format: [property, operator, value, scheme]
// Eval format: [jsCondition, scheme]
export type ColorRule = [string, Condition | ColorSchemeIn, Value?, ColorSchemeIn?];

export type ColorScheme = {
  background?: string,
  title?: string,
  text?: string,
  badges?: Array<Badge>,
}

export interface ColorRules {
  self?: ColorSchemeIn,
  front?: ColorSchemeIn,
  deadend?: ColorSchemeIn,
  fields?: Array<ColorRule>,
}

export interface Badge {

  id: string;

  imgUrl?: string;
  imgSize?: number;

  text?: string;
  fontSize?: number;

  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-middle' | 'bottom-middle';
  offsetX?: number;
  offsetY?: number;

  backgroundColor?: string;
  color?: string;
  borderRadius?: string;
  padding?: number;

}

export type ValueConstants = Record<string, Value>;

function parseSchemeIn(scheme: ColorSchemeIn): ColorScheme {
  if (typeof scheme === 'string') return {
    background: scheme,
  };

  const result: ColorScheme = {};

  if (scheme.background) result.background = scheme.background;
  if (scheme.title) result.title = scheme.title;
  if (scheme.text) result.text = scheme.text;

  if (scheme.badge) {
    if (scheme.badge instanceof Array) {
      result.badges = scheme.badge.map(badge => getFullBadgeDefinition(badge));
    } else {
      result.badges = [getFullBadgeDefinition(scheme.badge)];
    }
  }

  function getFullBadgeDefinition(badgeIn: string | Badge): Badge {

    if (Array.isArray(badgeIn))
      throw new Error('Singl badge definition cannot be an array!');

    let result : Badge | undefined = undefined;

    const textDefaults : Badge = {
      id: '',
      text: undefined,
      position: 'bottom-middle',
      color: 'rgba(var(--v-theme-on-warning))',
      backgroundColor: 'rgba(var(--v-theme-warning))',
      borderRadius: '5px',
      padding: 5,
      fontSize: 12,
      offsetY: -10,
    };

    const imgDefaults : Badge = {
      id: '',
      imgUrl: undefined,
      position: 'top-right',
      imgSize: 40,
      offsetX: -20,
      offsetY: -20,
      borderRadius: '50%',
      backgroundColor: 'rgba(var(--v-theme-accent))',
    };

    if (typeof badgeIn === 'string') {
      if (badgeIn.startsWith('http') || badgeIn.startsWith('/')) {
        const imgUrl = badgeIn;
        result = { ... imgDefaults, id: imgUrl, imgUrl };
      } else {
        const text = badgeIn;
        result = { ... textDefaults, id: text, text };
      }
    }

    if (typeof badgeIn === 'object') {
      const badge = badgeIn;
      if (badge.imgUrl) result = { ... imgDefaults, ... badge };
      if (badge.text) result = { ... textDefaults, ... badge };
    }

    if (! result)
      throw new Error('Badge definition error!');

    if (! result.id )
      result.id = result.imgUrl || result.text || '';

    return result;
  }

  return result;
}

// Convert TableGridItem to a simple object with namespaced properties for evaluation
function tableGridItemToObject(data: TableGridItem) {
  return Object.keys(data).reduce((acc, key) => {
    // split key by dot
    const [prefix, name] = key.split('.');

    if (!acc[prefix]) {
      acc[prefix] = {};
    }
    if (data[key]?.raw !== undefined) {
      acc[prefix][name] = data[key].raw;
    }
    return acc;
  }, {} as Record<string, Record<string, any>>);
}

function toCamelCase(str: string): string {
  return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
}

// Create a function to evaluate a JS condition against data
function createConditionEvaluator(dataItem: TableGridItem) {
  const data = tableGridItemToObject(dataItem);

  const keys = Object.keys(data);
  // eslint-disable-next-line prefer-template
  const code = keys.map((key) => `const ${toCamelCase(key)} = ${JSON.stringify(data[key])};`).join("\n\r") + '\n\r return ';

  return function evaluateCondition(condition: string): boolean {
    const codeCondition = `${code}${condition};`;

    try {
      const fn = new Function(codeCondition);
      const result = fn();

      return !!result;
    } catch (e) {
      console.error('CC: Failed to evaluate condition', condition, e);
      return false;
    }
  };
}

export function getMatchedRules(rules: Array<ColorRule>, item: TableGridItem, valueConstants?: ValueConstants) {
  const matchedRules: ColorSchemeIn[] = [];
  const evaluateCondition = createConditionEvaluator(item);

  for (const rule of rules) {
    // Skip undefined or null rules
    if (!rule) continue;
    // Handle eval expression format: [jsCondition, scheme]
    if (rule.length === 2 && typeof rule[0] === 'string' &&
        (typeof rule[1] === 'string' || typeof rule[1] === 'object')) {
      const [jsCondition, scheme] = rule;

      if (evaluateCondition(jsCondition)) {
        matchedRules.push(scheme);
      }
      continue;
    }

    // Handle standard format: [property, operator, value, scheme]
    const [prop, condition, conditionValueSrc, scheme] = rule as [string, Condition, Value, ColorSchemeIn];

    // Skip invalid or incomplete rows created by default UIs
    // - empty/undefined property
    // - missing/invalid operator
    // - empty/undefined scheme
    if (!prop || (typeof prop === 'string' && prop.trim() === '')) continue;
    if (!condition || !CONDITION_OPERATORS.includes(condition as Condition)) continue;
    if (
      !scheme ||
      (typeof scheme === 'string' && scheme.trim() === '') ||
      (typeof scheme === 'object' && Object.keys(scheme).length === 0)
    ) continue;

    let value = item[prop]?.raw;
    if (typeof value === 'object') continue;

    if (typeof value === 'string' && /^[.,\d]+$/.test(value))
      value = Number(value);

    const conditionValue = replaceTemplates(conditionValueSrc, valueConstants);

    if (condition === '=') {
      // eslint-disable-next-line eqeqeq
      if (value == conditionValue)
        matchedRules.push(scheme);
      continue;
    }

    if (condition === 'contains') {
      if (typeof value === 'string' && value.includes(String(conditionValue)))
        matchedRules.push(scheme);
      continue;
    }

    if (condition === 'one_of') {
      if (typeof conditionValue !== 'string')
        throw new Error('one_of condition value must be a comma separated values string!');
      const values = conditionValue.split(',').map(v => v.trim());
      if (values.includes(String(value)))
        matchedRules.push(scheme);
      continue;
    }

    if (typeof value === 'string' || typeof value === 'boolean' || value === undefined || value === null) continue;

    if (condition === '>') {
      if (value > Number(conditionValue))
        matchedRules.push(scheme);
      continue;
    }

    if (condition === '<') {
      if (value < Number(conditionValue))
        matchedRules.push(scheme);
      continue;
    }

    if (condition === '!=') {
      // eslint-disable-next-line eqeqeq
      if (value != conditionValue)
        matchedRules.push(scheme);
      continue;
    }

    if (condition === '<=') {
      if (value <= Number(conditionValue))
        matchedRules.push(scheme);
      continue;
    }

    if (condition === '>=') {
      if (value >= Number(conditionValue))
        matchedRules.push(scheme);
      continue;
    }
  }

  return matchedRules;
}

export function getColorScheme(item: TableGridItem, rules?: ColorRules, valueConstants?: ValueConstants) {

  if (!rules) return undefined;

  let resultScheme: ColorScheme | undefined = undefined;

  const level = Item(item).level();

  if (level === 0) merge(rules?.self);
  if (level === 1) merge(rules?.front);
  if (Item(item).kids() === 0) merge(rules?.deadend);

  if (!rules?.fields || rules?.fields.length === 0)
    return resultScheme;

  // Filter out undefined values from fields array
  const validFields = rules.fields.filter(field => field !== undefined && field !== null);
  if (validFields.length === 0)
    return resultScheme;

  const matchedFieldRules = getMatchedRules(validFields, item, valueConstants);
  matchedFieldRules.forEach(scheme => merge(scheme));

  function merge(scheme?: ColorSchemeIn) {

    if (!scheme) return;

    const newScheme = parseSchemeIn(scheme);

    if (!resultScheme) {
      resultScheme = newScheme;
      return;
    }

    (['background', 'title', 'text'] as Array<keyof Omit<ColorScheme, 'badges'>>).forEach(prop => {
      if (newScheme[prop])
        resultScheme![prop] = newScheme[prop];
    });

    if (newScheme.badges) {
      if (!resultScheme.badges)
        resultScheme.badges = newScheme.badges;
      else {
        // merging badges by id, so we can disable badges by setting imgUrl and text to undefined
        newScheme.badges.forEach(newBadge => {
          const index = resultScheme!.badges?.findIndex(badge => badge.id === newBadge.id);
          if (index === -1 || !index)
            resultScheme!.badges?.push(newBadge);
          else
            resultScheme!.badges![index] = newBadge;
        });
      }
    }

  }

  return resultScheme;
}

function replaceTemplates(value: Value, valueConstants?: ValueConstants) {
  if (!valueConstants) return value;
  if (typeof value !== 'string') return value;
  if (!value.startsWith('$')) return value;
  return valueConstants[value] || value;
}
